import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, ReportService, UtilityService } from 'app/core/core.module';
import { Part, PartAlias, PartAliasType, PartAttachment, PartConfigurationLine, PartOption, PartPartServiceChargeLink, PartServiceCharge } from 'app/core/model/entity-model';
import { Note } from 'app/core/model/note';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { PartTypeSelectDialogComponent, SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent, DxTreeListComponent } from 'devextreme-angular';
import { alert, confirm, custom } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AuditDialogComponent } from '../../shared/audit-dialog/audit-dialog.component';
import { PartSearchComponent } from '../part-search/part-search.component';
import { ServiceChargeDialogComponent } from '../service-charge-dialog/service-charge-dialog.component';
import { PartAliasDialogComponent } from '../part-alias-dialog/part-alias-dialog.component';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-part-detail',
    templateUrl: './part-detail.component.html',
    styleUrls: ['./part-detail.component.scss'],
    providers: [GenesisUnitOfWork],
    standalone: false,
})
export class PartDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    partId: any;
    part: Part;
    parts: any;
    orderLines: any;
    serviceChargeLinks: PartPartServiceChargeLink[];
    autoExpand: boolean;
    options: any[];
    childrenParts: Part[];
    partAliases: PartAlias[];
    costHistory: Note[] = [];
    partAttachments: PartAttachment[];
    partCost = 0.0;
    partsCalculatedCost = 0.0;
    servicesCost = 0.0;
    showVinPrefix = false;

    unitOfMeasures: any[];
    shippingMethods: any[];

    userAccess = false;
    currentUser: any;
    readOnly = true;
    readyToDeliver = false;
    isToastVisible = false;

    showAsDialog = false;
    canSeeCost = false;
    canSeePricing = false;
    canUpdateCost = false;
    canUpdatePricing = false;
    canEditPartTree = false;
    partTreeChanged = false;
    frames: any[] = [];
    quickBooksAccounts: any;
    vendors: any;
    expanded = false;

    partConfigurationLine: PartConfigurationLine;
    createSubPartMode = false;

    uploadUrl = `${environment.serviceName}/file/SavePartAttachmentToBlobStorage`;
    uploadHeader: any;

    @ViewChild('optionsGrid') optionsGrid: DxDataGridComponent;
    @ViewChild('childrenGrid') childrenGrid: DxDataGridComponent;
    @ViewChild('servicesGrid') servicesGrid: DxDataGridComponent;
    @ViewChild('subPartsTree') subPartsTree: DxTreeListComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private reportService: ReportService,
        private utility: UtilityService,
    ) {}

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        if (!this.showAsDialog) this.partId = this.route.snapshot.params.id;
        this.autoExpand = true;
        this.vendors = await this.uow.getVendors();
        const types = this.uow.lookups.partAliasTypes;
        this.quickBooksAccounts = this.uow.lookups.quickBooksAccounts;
        const quickBooksItems = await this.uow.purchaseOrderLineItems.whereActive();

        this.setPermissions();

        await this.getFrames();
        await this.getData();
        this.sortPartOptions(this.options);
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges() && !this.partTreeChanged;
    }
    setPermissions() {
        this.canSeeCost = (this.auth.hasRole('Parts:CanSeeCost') || this.auth.hasRole('Parts:Admin')) && !this.showAsDialog;
        this.canUpdateCost = this.auth.hasRole('Parts:CanUpdateCost') && !this.showAsDialog;

        this.canSeePricing = (this.auth.hasRole('Parts:CanSeePricing') || this.auth.hasRole('Parts:Admin')) && !this.showAsDialog;
        this.canUpdatePricing = this.auth.hasRole('Parts:CanUpdatePricing') && !this.showAsDialog;
        this.readOnly = !this.auth.hasRole('Parts:Admin');
        this.canEditPartTree = this.auth.hasRole('Parts:CanEditPartTree');
    }

    async getFrames() {
        this.frames = await this.uow.getFrames();
    }

    async refresh() {
        await this.getData();
    }

    async getData() {
        this.appInfo.pleaseWaitShow();

        if (this.partId === 'new') {
            this.part = this.uow.parts.createEntity() as Part;
            this.part.reportGuid = crypto.randomUUID();
            this.part.created = new Date();
            this.part.inStock = 0;
            this.orderLines = [];
            if (this.createSubPartMode) {
                this.part.partNumber = this.partConfigurationLine.partNumber;
                this.part.mfgPartNumber = this.partConfigurationLine.partNumber;
                this.part.description = this.partConfigurationLine.description;
                this.part.mfgDescription = this.partConfigurationLine.description;
            }
        } else {
            this.part = (await this.uow.parts.byId(this.partId, 'partLinks, partPartTypes.partType')) as Part;
            const orderLine = await this.uow.createOrderLine(+this.partId, null, 1, 1, null, null, true);
            if (!orderLine) this.orderLines = [];
            else this.orderLines = orderLine.children;
        }
        this.appInfo.pleaseWaitHide();

        if (!!this.part.category && this.part.category.indexOf('Finished Product') !== -1) this.showVinPrefix = true;
        await this.getOptions();
        await this.getChildren();
        await this.getPartAliases();
        await this.getServiceChargeLinks();
        await this.getAttachments();
        await this.getCostHistory();
        this.calculateCosts();
        this.appInfo.pleaseWaitHide();
    }

    async getCostHistory() {
        this.costHistory = this.part.id ? await this.uow.getNotes('PartCost', this.part.id) : [];
    }

    async getOptions() {
        const options = await this.uow.getPartOptionsByPartId(this.part.id);
        this.options = options;
    }

    async getChildren() {
        this.childrenParts = (await this.uow.getPartChildren(this.part.id)) as Part[];
    }

    async getPartAliases() {
        this.partAliases = await this.uow.getPartAliases(this.part.id);
    }

    async getServiceChargeLinks() {
        this.serviceChargeLinks = await this.uow.getPartServiceChargeLinks(this.part.id);
        this.calculateCosts();
    }

    async getAttachments() {
        this.partAttachments = (await this.uow.getPartAttachments(this.part.id)) as PartAttachment[];
    }

    calculateCosts() {
        this.servicesCost = 0;
        this.partsCalculatedCost = 0;

        this.serviceChargeLinks.forEach((service) => {
            if (service.deleted) return;
            this.servicesCost += service.cost;
        });

        let totalPartCost = 0.0;

        if (this.orderLines.every((line) => line.deleted)) this.partsCalculatedCost = this.part.cost;
        else {
            this.orderLines.forEach((line) => {
                if (line.deleted) return;
                this.partsCalculatedCost += line.extendedQuantity * line.calculatedCost;
            });
        }

        totalPartCost = this.servicesCost + this.partsCalculatedCost;
        if (this.partCost !== totalPartCost) {
            this.partCost = totalPartCost;
        }
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    printPartDetail(topLevelOnly?: boolean) {
        this.reportService.openPartDetailReport(this.part.reportGuid, topLevelOnly);
    }

    partParentDetailRedirect(id: any) {
        if (id && id > 0) {
            const url = `/parts/${id}`;
            window.open(url, '_blank');
        }
    }

    onReorder = async (e) => {
        var visibleRows = e.component.getVisibleRows(),
            toIndex = this.options.indexOf(visibleRows[e.toIndex].data),
            fromIndex = this.options.indexOf(e.itemData);

        this.options.splice(fromIndex, 1);
        this.options.splice(toIndex, 0, e.itemData);

        this.updateDisplayOrder(this.options);
        this.sortPartOptions(this.options);
        await this.uow.commit();
    };

    onVendorSelection = (e) => {
        console.log(e);
        if (!this.part.preferredVendor) {
            this.part.preferredVendor = e.data.companyName;
        }
    };

    updateDisplayOrder(partOptions: any[]) {
        partOptions.forEach((partOption, index) => {
            if (partOption.displayOrder !== index + 1) partOption.displayOrder = index + 1;
        });
    }

    sortPartOptions(partOptions: any[]) {
        partOptions.sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            location: 'before',
            template: 'toolbarButtons',
        });
    }
    expand() {
        this.expanded = !this.expanded;
        this.subPartsTree.instance.forEachNode((node) => {
            if (node.hasChildren) this.expanded ? this.subPartsTree.instance.expandRow(node.key) : this.subPartsTree.instance.collapseRow(node.key);
        });
    }

    async addMultiple() {
        await this.save();

        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partDetailMode: true,
                showSideBar: true,
                invalidId: this.part.id,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.partIds.length > 0) {
                this.appInfo.pleaseWaitShow();
                const partLinks = [];
                for (let index = 0; index < result.partIds.length; index++) {
                    const partLink: any = this.uow.partLinks.createEntity();
                    this.part.partLinks.push(partLink);
                    partLink.part = this.part;
                    partLink.partId = this.partId;
                    partLink.subPartId = result.partIds[index];
                    partLink.quantity = 0;
                    partLink.createdBy = this.auth.currentUser.userName;
                    partLink.created = new Date();
                    partLinks.push(partLink);
                }
                await this.save();
                this.partTreeChanged = true;
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async edit(selectedLink: any) {
        if (this.readOnly) return;
        const oldQuantity = selectedLink.quantity;
        const part = (await this.uow.parts.byId(selectedLink.partId)) as any;
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Part Link',
                dialogPrompt: 'Enter New Quantity',
                dialogValue: selectedLink.quantity,
                checkNumeric: true,
                checkInteger: false,
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                console.log('selectedLink', selectedLink);
                const partLink: any = await this.uow.partLinks.byId(selectedLink.partLinkIdSource);
                partLink.quantity = result.value;
                selectedLink.quantity = result.value;
                selectedLink.extendedQuantity = result.value * selectedLink.parentLine.quantity;
                await this.save();
                this.partTreeChanged = true;
                // await this.getData();
            }
            modalRef.hide();
        });
    }

    async editCost(selectedLink: any) {
        if (this.readOnly) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Cost',
                dialogPrompt: 'Enter New Cost',
                dialogValue: 0,
                checkNumeric: true,
                checkInteger: false,
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                const partLink: any = await this.uow.partLinks.byId(selectedLink.partLinkIdSource);
                const part = (await this.uow.parts.byId(partLink.subPartId)) as Part;
                part.cost = result.value;
                selectedLink.cost = result.value;
                await this.save();
                // await this.getData();
            }
            modalRef.hide();
        });
    }

    async delete(selectedLink: any) {
        const result = await (confirm(`Are you ready to DELETE this sub-part: ${selectedLink.partNumber}?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (result) {
            this.appInfo.pleaseWaitShow();

            const partLinkId = selectedLink.partLinkIdSource;
            const partLink = (await this.uow.partLinks.byId(partLinkId)) as any;
            partLink.deleted = new Date();
            partLink.deletedBy = this.auth.currentUser.userName;

            await this.save();
            this.partTreeChanged = true;
            await this.getData();
        }
    }

    async setAsAssembly(selectedLink: any) {
        const part = (await this.uow.parts.byId(selectedLink.partId)) as Part;
        part.isAssembly = !selectedLink.isAssembly;
        selectedLink.isAssembly = !selectedLink.isAssembly;
        await this.uow.commit();
        // await this.getData();
    }

    async submitChanges() {
        let result = '';
        const openOrders = (await this.uow.getOpenOrderLineDetailPartsByPartId(this.partId)) as any;
        const openOrdersCount = openOrders.length;
        if (openOrdersCount > 0) {
            const confirm = custom({
                title: `${this.appInfo.settings.company}`,
                messageHtml: `There ${openOrdersCount === 1 ? 'is' : 'are'} ${openOrdersCount} open ${openOrdersCount === 1 ? 'order' : 'orders'} with part # ${
                    this.part.partNumber
                }.\r\nDo you want to update all NEW orders?`,
                buttons: [
                    {
                        text: 'Yes',
                        onClick: (e) => {
                            return 'Yes';
                        },
                    },
                    {
                        text: 'No',
                        onClick: (e) => {
                            return 'No';
                        },
                    },
                    {
                        text: 'Cancel',
                        onClick: (e) => {
                            return 'Cancel';
                        },
                    },
                ],
            });
            result = await confirm.show();
        }
        if (result === 'Cancel') return;

        this.part.changeLog = `Part Tree was edited and submitted`;
        await this.save();
        this.partTreeChanged = false;
        this.appInfo.pleaseWaitShow();

        if (result === 'Yes') {
            this.part.changeLog = `Part Tree was edited and submitted`;
            await this.uow.commit();
            const detailIds = _.uniq(
                openOrders.map((x) => {
                    return x.orderLineDetailId;
                }),
            ) as any;

            //add the openOrders to the PartBulkEdit table
            const today = new Date();
            detailIds.forEach((id) => {
                this.uow.partBulkEdits.createEntity({
                    partId: this.part.id,
                    orderLineDetailId: id,
                    created: today,
                    createdBy: this.auth.currentUser.userName,
                });
            });
            await this.uow.commit();

            // await this.utility.processOrderLineDetails(detailIds);
        }
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    frameCheck() {
        return _.findIndex(this.orderLines, { partId: this.part.frameId }) !== -1;
    }

    async save(andConfirm?: boolean) {
        if (andConfirm && !!this.part.frameId && !this.frameCheck()) {
            const result = await confirm(`The selected frame is not in the part tree.  Continue?`, `${this.appInfo.settings.company}`);
            if (!result) return;
        }

        this.appInfo.pleaseWaitShow();
        this.isToastVisible = true;
        await this.uow.commit();
        if (this.partId === 'new' && !this.showAsDialog) {
            this.router.navigate(['../' + this.part.id], { relativeTo: this.route, replaceUrl: true, state: { navigateOnSaveNew: true } });
        }
        this.partId = this.part.id;
        this.appInfo.pleaseWaitHide();
    }

    async cancel() {
        if (!!this.partTreeChanged) {
            const result = await confirm(`There are active part tree changes. Are you sure you want to cancel?`, `${this.appInfo.settings.company}`);
            if (!result) return;
        }
        this.uow.rollback();
        this.close();
    }

    close() {
        if (this.showAsDialog) this.closeDialog();
        else {
            try {
                this.location.back();
            } catch {
                this.router.navigate(['../'], { relativeTo: this.route });
            }
        }
    }

    closeDialog() {
        if (this.showAsDialog) {
            this.uow.rollback();
            this.result.next({});
        }
    }

    async saveDialog() {
        if (this.showAsDialog) {
            await this.save();
            this.result.next({ partId: this.part.id });
        }
    }

    async addOption() {
        const existingOptionPartIds = this.options.map((option) => option.optionPartId);
        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partDetailMode: true,
                showSideBar: true,
                excludePartIds: existingOptionPartIds,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                const partIds = _(result.partIds).uniq().valueOf();
                for (let index = 0; index < partIds.length; index++) {
                    const partOption = this.uow.partOptions.createEntity() as PartOption;
                    partOption.part = this.part;
                    partOption.optionPartId = partIds[index];
                    this.options.push(partOption);
                }
                await this.save();
                await this.getOptions();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async deleteOption(item: any) {
        if (!item) return;
        const result = await (confirm(`Are you ready to DELETE this option?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (result) {
            this.appInfo.pleaseWaitShow();
            item.entityAspect.setDeleted();
            await this.uow.commit();
            await this.getOptions();
            this.appInfo.pleaseWaitHide();
        }
    }

    async addPartType() {
        const excludePartTypeIds = this.part.partPartTypes.map((partPartType) => {
            return partPartType.partTypeId;
        });
        const modalRef = this.modalService.show(PartTypeSelectDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                excludePartTypeIds: excludePartTypeIds,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                const partPartType: any = this.uow.partPartTypes.createEntity();
                partPartType.partTypeId = result.partTypeId;
                const partType: any = await this.uow.partTypes.byId(result.partTypeId);
                partType.partPartTypes.push(partPartType);
                this.part.partPartTypes.push(partPartType);
                await this.save();
            }
            modalRef.hide();
        });
    }

    async deletePartType(partPartType: any) {
        if (!partPartType) return;
        const result = await (confirm(`Are you ready to Remove this Part Type?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (result) {
            this.appInfo.pleaseWaitShow();
            partPartType.entityAspect.setDeleted();
            await this.uow.commit();
            this.appInfo.pleaseWaitHide();
        }
    }

    async addChildPart() {
        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partDetailMode: true,
                showSideBar: true,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                for (let index = 0; index < result.partIds.length; index++) {
                    const childPart = (await this.uow.parts.byId(result.partIds[index])) as Part;
                    childPart.parentPartId = this.part.id;
                    if (childPart.inStock && childPart.inStock !== 0) {
                        const partPurchase = this.uow.partPurchases.createEntity() as any;
                        partPurchase.partId = this.part.id;
                        partPurchase.date = new Date();
                        partPurchase.quantity = childPart.inStock;
                        partPurchase.price = childPart.cost;
                        partPurchase.userId = this.auth.currentUser.id;
                        partPurchase.reason = `Added Child Part (Part Id=${childPart.id}; Part Number=${childPart.partNumber}; Qty=${childPart.inStock})`;
                        this.part.inStock = this.part.inStock + childPart.inStock;
                        this.part.changeLog = `Added Child InStock Qty: ChildPartId: ${childPart.id} --- Qty=${childPart.inStock}`;
                        childPart.inStock = 0;
                    }
                }
                await this.save();
                await this.getChildren();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async deleteChildPart(item: any) {
        if (!item) return;
        const result = await (confirm(`Are you ready to DELETE this child?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!result) return;
        this.appInfo.pleaseWaitShow();
        item.parentPartId = null;
        await this.uow.commit();
        await this.getChildren();
        this.appInfo.pleaseWaitHide();
    }

    async auditLog(id?, entityTypeName?) {
        const ids = [];
        if (id) ids.push(id);
        else ids.push(this.part.id);
        const auditLogs = [];

        const partAudits = await this.uow.getHistory('Part', [this.part.id]);
        partAudits.forEach((partAudit) => {
            partAudit.changes.forEach((change) => {
                if (change.columnName === 'ChangeLog') change.originalValue = '...';
            });
            auditLogs.push(partAudit);
        });

        const partPurchaseIds = (await this.uow.getPartPurchasesByPartId(this.part.id)).map((x) => x.id);
        if (partPurchaseIds && partPurchaseIds.length > 0) {
            const partPurchaseAudits = await this.uow.getHistory('PartPurchase', partPurchaseIds);
            partPurchaseAudits.forEach((x) => {
                x.entityTypeName = 'Inventory Adjustment';
                auditLogs.push(x);
            });
        }
        const modalRef = this.modalService.show(AuditDialogComponent, {
            class: 'modal-mdw',
            initialState: {
                auditLogs: auditLogs,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
            }
            modalRef.hide();
        });
    }

    async convertToService() {
        const selectedLine = this.subPartsTree.instance.getSelectedRowsData()[0];
        const cost = selectedLine.extendedQuantity * selectedLine.cost;

        const result = await this.addServiceChargeDialog(null, cost);
        if (result) this.delete(selectedLine);
    }

    async addServiceCharge(serviceCharge?: PartPartServiceChargeLink) {
        await this.addServiceChargeDialog(serviceCharge);
    }

    async addServiceChargeDialog(serviceCharge?: PartPartServiceChargeLink, cost?: number): Promise<boolean> {
        let partServiceCharge;
        if (!serviceCharge) {
            partServiceCharge = this.uow.partPartServiceChargeLinks.createEntity({
                partId: this.part.id,
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
            });
        } else partServiceCharge = serviceCharge;
        if (cost) partServiceCharge.cost = cost;

        const modalRef = this.modalService.show(ServiceChargeDialogComponent, {
            class: 'modal-sm',
            initialState: {
                serviceCharge: partServiceCharge,
            },
        });

        const promise = new Promise<boolean>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.success) {
                    await this.save();
                    await this.getServiceChargeLinks();
                    resolve(true);
                } else {
                    this.uow.rollback();
                }
                modalRef.hide();
                resolve(false);
            });
        });
        return promise;
    }

    async deleteServiceCharge(serviceChargeLink: PartPartServiceChargeLink) {
        if (!serviceChargeLink) return;

        const success = await confirm(`Are you sure you want to DELETE this service charge?`, this.appInfo.settings.company);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        serviceChargeLink.deleted = new Date();
        serviceChargeLink.deletedBy = this.auth.currentUser.userName;
        await this.save();
        await this.getServiceChargeLinks();
        this.appInfo.pleaseWaitHide();
    }

    onAliasDoubleClick(e) {
        this.editAlias(e.data);
    }

    async editAlias(partAlias?: PartAlias) {
        let alias = partAlias;
        if (!alias) {
            alias = this.uow.partAliases.createEntity({
                partAliasTypeId: PartAliasType.Types.PurchaseOrder,
                partId: this.part.id,
                vendorId: null,
                partNumber: this.part.partNumber,
                cost: this.part.cost,
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                quickBooksLineItemId: 2,
            }) as PartAlias;
        }

        const modalRef = this.modalService.show(PartAliasDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                part: this.part,
                partAlias: alias,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getPartAliases();
            }
            this.appInfo.pleaseWaitHide();
            modalRef.hide();
        });
    }

    async deleteAlias(partAlias: PartAlias) {
        if (!partAlias) return;

        const success = await confirm(`Are you sure you want to DELETE this part alias?`, this.appInfo.settings.company);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        partAlias.deleted = new Date();
        partAlias.deletedBy = this.auth.currentUser.userName;
        await this.save();
        await this.getPartAliases();
        this.appInfo.pleaseWaitHide();
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        if (e?.request?.responseText) {
            const fileResponses = JSON.parse(e.request.responseText) as { filename: string; name: string }[];
            for (const fileResponse of fileResponses) {
                const attachment = this.uow.partAttachments.createEntity({
                    partId: this.part.id,
                    name: fileResponse.name,
                    filename: fileResponse.filename,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                });
                this.partAttachments.push(attachment);
                await this.uow.commit([attachment]);
            }
            this.partAttachments = _(this.partAttachments).orderBy('created', 'desc').valueOf();
        }
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        this.appInfo.pleaseWaitHide();
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    async removeAttachment(e: any, attachment: PartAttachment) {
        e.preventDefault();
        const success = await confirm(`Are you sure you want to DELETE the attachment ${attachment.name}?`, this.appInfo.settings.company);
        if (!success) return;

        attachment.deleted = new Date();
        attachment.deletedBy = this.auth.currentUser.userName;
        this.partAttachments.splice(this.partAttachments.indexOf(attachment), 1);
        await this.uow.commit([attachment]);
    }

    openAttachment(attachment) {
        window.open(attachment.filename, '_blank');
    }
}
